import { Switch as Base } from '@base-ui/react/switch'

import { cn } from '../../lib/cn'
import s from './Switch.module.css'

/**
 * Interrupteur — report de `.switch` du prototype validé, où il porte tous les réglages : les
 * bascules de « Réglages » côté back-office, celles des préférences côté terrain.
 *
 * ⚠️ **Ce n'est pas une `Checkbox`, et la distinction n'est pas cosmétique.** Une case à cocher
 * dit « ceci fera partie de l'envoi » ; un interrupteur dit « ceci est allumé, maintenant ». Les
 * « Réglages » rendaient des cases faute de cette primitive — le libellé promettait un état, la
 * forme annonçait une sélection.
 *
 * **Primitive `@base-ui/react`**, comme `Checkbox` : elle publie le rôle `switch`, l'active à
 * l'Espace comme à l'Entrée, et pose son état sur le DOM (`data-checked`, `data-unchecked`,
 * `data-disabled`) — lu ici **en CSS**, aucun état React transformé en nom de classe.
 *
 * ⚠️ Les noms d'attributs viennent de `SwitchRootDataAttributes` du paquet, pas d'une supposition :
 * `packages/ui/CLAUDE.md` prévient qu'ils varient d'une primitive à l'autre.
 *
 * **Contrôlé seulement** (`checked` + `onCheckedChange`), comme `Checkbox` et `Modal` : un réglage
 * appartient à l'écran qui l'enregistre. Un état interne le ferait basculer à l'œil avant que le
 * serveur ait accepté — et rester basculé s'il refuse.
 */

export interface SwitchProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  /** Rattachement à un `<label htmlFor>` externe — la forme des rangées de « Réglages ». */
  id?: string
  /** Rattachement à un libellé déjà rendu ailleurs. */
  'aria-labelledby'?: string
  /**
   * Le nom accessible **quand aucun libellé n'est rendu à côté**.
   *
   * ⚠️ À n'employer que dans ce cas : un contrôle qui a un libellé visible se rattache par `id` ou
   * `aria-labelledby`, sinon la synthèse vocale annonce une chose et l'œil en lit une autre.
   */
  'aria-label'?: string
  /**
   * Le texte qui explique **ce que l'interrupteur commande** — la ligne grise sous le libellé,
   * dans une rangée de réglage. Le libellé dit quoi, cette phrase dit ce qui changera : sans ce
   * rattachement, la synthèse vocale annonce « Photos annexées, interrupteur » et laisse la
   * conséquence à l'écran, pour l'œil seulement.
   */
  'aria-describedby'?: string
  disabled?: boolean
  className?: string
}

export function Switch({
  checked,
  onCheckedChange,
  id,
  'aria-labelledby': labelledBy,
  'aria-label': label,
  'aria-describedby': describedBy,
  disabled,
  className,
}: SwitchProps) {
  return (
    <Base.Root
      id={id}
      aria-labelledby={labelledBy}
      aria-label={label}
      aria-describedby={describedBy}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(s.root, className)}
    >
      <Base.Thumb className={s.thumb} />
    </Base.Root>
  )
}
